import { Input, Modal, Table, TableColumnsType } from 'antd';
import React, { useEffect, useState } from 'react';
import { crossIcon, resetIcon } from './common/Icons';
import { IMilestoneCategory, IMilestoneData } from '../api/getMilestone';
import { IMilestoneHistory, IMilestoneStatus } from './common/types/milestones';
import AntSelect from './prescriptionPad/rightPanel/workspace/padV2/AntSelect';
import { allMilestoneOptions, mileStoneCategoryName } from './common/constants/milestones';
import { AntTooltip } from './common/AntTooltip';
import AgeSelector from './AgeSelector';
import { useDispatch } from 'react-redux';
import { openMilestonConfimationDialog } from './common/milestoneModals';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';

const column: TableColumnsType<any> = [
    {
        title: <p className="text-12 text-darwin-neutral-800 uppercase font-700">Milestone</p>,
        dataIndex: 'milestone',
        key: 'milestone',
        fixed: 'left',
    },
    {
        title: <p className="text-12 text-darwin-neutral-800 uppercase font-700">Category</p>,
        dataIndex: 'category',
        key: 'category',
        width: '10%',
    },
    {
        title: <p className="text-12 text-darwin-neutral-800 uppercase font-700">Status</p>,
        dataIndex: 'status',
        key: 'status',
        width: '23%',
    },
    {
        title: <p className="text-12 text-darwin-neutral-800 uppercase font-700">Age</p>,
        dataIndex: 'age',
        key: 'age',
        width: '15%',
    },
    {
        title: <p className="text-12 text-darwin-neutral-800 uppercase font-700">Notes (if any)</p>,
        dataIndex: 'notes',
        key: 'notes',
        width: '20%',
    },
    {
        title: '',
        dataIndex: 'delete',
        key: 'delete',
        width: '5%',
    },
];

const MilestoneSelectionModal: React.FC<{
    isVisible: boolean;
    closeModal: (prevStatus?: string) => void;
    title?: string;
    selectedMilestones?: IMilestoneData[];
    initialStatus: IMilestoneHistory[];
    saveMileStoneBulk: (result: IMilestoneHistory[]) => void;
    clearSelectedMilestones: () => void;
    modalStatus?: IMilestoneStatus | undefined;
    markPreviousMilestonesAsAchieved: (
        status: IMilestoneStatus,
        categoryId?: IMilestoneCategory,
        ageGroup?: string,
        markprev?: boolean,
    ) => void;
    getAllPrevMilestones: (
        categoryId?: IMilestoneCategory,
        ageGroup?: string,
        markprev?: boolean,
    ) => IMilestoneData[] | undefined;
    markedMilestones: IMilestoneHistory[];
    getMilestoneSummary: (
        milestonesTillCurrAge: IMilestoneData[],
        markedMilestonesAll: IMilestoneHistory[],
    ) => {
        statuses: IMilestoneHistory[];
        result?: any;
    };
}> = ({
    isVisible = false,
    closeModal,
    title,
    selectedMilestones,
    initialStatus,
    saveMileStoneBulk,
    clearSelectedMilestones,
    modalStatus,
    markPreviousMilestonesAsAchieved,
    getAllPrevMilestones,
    markedMilestones,
    getMilestoneSummary,
}) => {
    const [milestoneStatus, setMilestoneStatus] = useState<IMilestoneHistory[]>([]);
    const dispatch = useDispatch();

    const setMilestone = (mid: string, payload: Partial<IMilestoneHistory>) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setMilestoneStatus((prev) => {
            const currVac = prev?.find((val) => val.id === mid);
            return [...prev.filter((p) => p.id !== mid), { ...currVac, ...payload }];
        });
    };

    useEffect(() => {
        setMilestoneStatus(initialStatus);
    }, [isVisible]);

    const saveMileStone = () => saveMileStoneBulk(milestoneStatus);

    const highestAgeGroup = selectedMilestones?.reduce(
        (prev, curr) => (prev && prev?.maxHrsFromBirth > curr?.maxHrsFromBirth ? prev : curr),
        undefined as IMilestoneData | undefined,
    );

    const markMilestones = () => {
        const groupByCat = Object.values(groupBy(milestoneStatus, 'categoryId')).reduce(
            (prev, curr) => {
                const data = getAllPrevMilestones(
                    undefined,
                    highestAgeGroup?.ageGroup,
                    highestAgeGroup?.ageGroup ? false : true,
                );

                const summary = data ? getMilestoneSummary(data, markedMilestones) : undefined;

                if (summary?.result?.[curr[0].categoryId]?.achieved === true) {
                    return prev;
                }

                const hasAchieved = curr.find(
                    (val) =>
                        val.status === 'achieved' ||
                        val.status === 'achieved_early' ||
                        val.status === 'achieved_delay' ||
                        val.status === 'achieved_unknown',
                );

                if (hasAchieved) prev[curr[0].categoryId as IMilestoneCategory] = true;
                return prev;
            },
            {} as Record<IMilestoneCategory, boolean>,
        );

        saveMileStone();
        clearSelectedMilestones();
        closeModal();

        if (groupByCat && !isEmpty(groupByCat)) {
            dispatch(
                openMilestonConfimationDialog(
                    () => {
                        Object.keys(groupByCat).forEach((key) => {
                            if (groupByCat[key as IMilestoneCategory] === true) {
                                markPreviousMilestonesAsAchieved(
                                    'achieved_unknown' as IMilestoneStatus,
                                    key as IMilestoneCategory,
                                    highestAgeGroup?.ageGroup,
                                    highestAgeGroup?.ageGroup ? false : true,
                                );
                            }
                        });
                    },
                    undefined,
                    Object.keys(groupByCat) as IMilestoneCategory[],
                ),
            );
        }
    };

    const data = selectedMilestones
        ? selectedMilestones.map((v, idx) => {
              const currStatus = milestoneStatus?.find((val) => val.id === v.id);

              return {
                  key: idx + 1,
                  milestone: <p className="text-13 font-700">{v.name}</p>,
                  category: (
                      <p className="font-700 text-13">{mileStoneCategoryName[v.categoryId]}</p>
                  ),
                  status: (
                      <div className="flex items-center w-full">
                          <AntSelect
                              options={allMilestoneOptions
                                  .map((val) => {
                                      return {
                                          value: (
                                              <span className="flex w-full h-full items-center gap-8">
                                                  <span className="flex-shrink-0">{val.icon}</span>
                                                  <p className="text-14">{val.title}</p>
                                              </span>
                                          ),
                                          id: val.status as string,
                                      };
                                  })}
                              getValue={(o) => o.id}
                              getLabelToCompare={(o) => o.value}
                              placeholder="Select Status"
                              className="w-full rounded-4"
                              setValue={(option) => {
                                  setMilestone(v.id, {
                                      status: option?.id as IMilestoneStatus,
                                  });
                              }}
                              showSearch
                              allowClear
                              value={
                                  currStatus?.status
                                      ? {
                                            id: currStatus.status,
                                            value: (
                                                <span className="flex h-full w-full items-center gap-8">
                                                    <span className="flex-shrink-0">
                                                        {
                                                            allMilestoneOptions.find(
                                                                (b) =>
                                                                    b.status === currStatus.status,
                                                            )?.icon
                                                        }
                                                    </span>
                                                    <p className="text-14">
                                                        {
                                                            allMilestoneOptions.find(
                                                                (b) =>
                                                                    b.status === currStatus.status,
                                                            )?.optionTitle
                                                        }
                                                    </p>
                                                </span>
                                            ),
                                        }
                                      : undefined
                              }
                          />
                      </div>
                  ),
                  age: (
                      <div className={`flex hover-parent-element copy-age-${idx} flex-col`}>
                          <AgeSelector
                              value={currStatus?.age}
                              onSelect={(value: { unit?: string; value: string }) => {
                                  setMilestone(v.id, { age: value });
                              }}
                              disabled={
                                  currStatus?.status === 'delayed' ||
                                  currStatus?.status === 'under_observation'
                              }
                          />
                      </div>
                  ),
                  notes: (
                      <div className={`flex hover-parent-element copy-note-${idx} flex-col`}>
                          <Input.TextArea
                              onChange={(e: any) => {
                                  setMilestone(v.id, { notes: e.target.value });
                              }}
                              rows={1}
                              className="rounded-4"
                              placeholder="Notes..."
                              defaultValue={currStatus ? currStatus.notes : undefined}
                              value={currStatus?.notes}
                          />
                      </div>
                  ),
                  delete: (
                      <AntTooltip content="Reset">
                          <span
                              role="img"
                              aria-label="delete"
                              className="anticon cursor-pointer anticon-delete"
                              style={{ color: 'rgb(97, 106, 143)' }}
                              onClick={() => {
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  setMilestoneStatus((prev) => {
                                      const currvaccStatus = currStatus ?? {};

                                      const currInitStatus = {
                                          ...currvaccStatus,
                                          status: undefined,
                                          notes: undefined,
                                          age: undefined,
                                      };

                                      return [
                                          ...prev.filter((p) => p.id !== v.id),
                                          { ...(currInitStatus ?? {}) },
                                      ];
                                  });
                              }}
                          >
                              {resetIcon('w-16 h-16')}
                          </span>
                      </AntTooltip>
                  ),
              };
          })
        : [];

    return (
        <Modal
            closeIcon={() => null}
            closable={false}
            destroyOnClose
            visible={isVisible}
            centered
            footer={
                <div className="flex py-8 items-center w-full justify-between">
                    <div
                        onClick={() => closeModal(modalStatus)}
                        className="px-16 py-8 btn-small cursor-pointer rounded-8 bg-white border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 text-14 font-700"
                    >
                        Close
                    </div>
                    <div
                        onClick={markMilestones}
                        className="px-16 py-8 btn-small cursor-pointer rounded-8 bg-darwin-accent-bright-blue-800 text-white text-14 font-700"
                    >
                        Done
                    </div>
                </div>
            }
            bodyStyle={{
                padding: 0,
                height: 476,
                borderRadius: 4,
                overflowY: 'hidden',
                maxHeight: 'calc(100vh - 200px)',
            }}
            onCancel={() => closeModal(modalStatus)}
            width="80%"
        >
            <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                <div className="p-16 border-b border-darwin-neutral_new-200 py-12 flex items-center justify-between">
                    <p className="text-20">{title ?? 'Update milestones'}</p>
                    <span onClick={() => closeModal(modalStatus)} className="cursor-pointer">
                        {crossIcon('w-14 h-14')}
                    </span>
                </div>
                <div className="px-12 flex flex-col w-full py-16 max-h-420 overflow-auto">
                    {selectedMilestones?.length && (
                        <Table
                            dataSource={data}
                            pagination={false}
                            columns={column}
                            sticky={true}
                            scroll={{ x: 740 }}
                            bordered={true}
                            className="overflow-auto milestone-selection-table border rounded-4 overflow-auto border-darwin-neutral_new-50 hide-scrollbar"
                        />
                    )}

                    <style>
                        {`
                          .milestone-selection-table {
                            .ant-table-thead {
                              .ant-table-cell {
                                  background: #F4F4F4;
                              }
                            }
                          }
                        `}
                    </style>
                </div>
            </div>
        </Modal>
    );
};

export default MilestoneSelectionModal;
